import { useCallback, useContext, useMemo, useRef } from 'react'
import type {
  ChangeEvent,
  MouseEvent as ReactMouseEvent,
  ReactElement,
} from 'react'
import * as z from 'zod'
import { DatePicker } from '../../../../components'
import { useFieldProps } from '../../hooks'
import type {
  FieldProps,
  Validator,
  ValidatorWithCustomValidators,
  Schema,
} from '../../types'
import { pickSpacingProps } from '../../../../components/flex/utils'
import clsx from 'clsx'
import type { FieldBlockProps } from '../../FieldBlock'
import FieldBlock from '../../FieldBlock'
import SharedContext from '../../../../shared/Context'
import { parseISO, isValid, isBefore, isAfter, startOfDay } from 'date-fns'
import useTranslation from '../../hooks/useTranslation'
import type {
  DatePickerEvent,
  DatePickerProps,
  DisplayPickerEvent,
} from '../../../../components/DatePicker'
import { convertStringToDate } from '../../../../components/date-picker/DatePickerCalc'
import type { ProviderProps } from '../../../../shared/Provider'
import { FormError } from '../../utils'
import type { DatePickerInvalidDates } from '../../../../components/date-picker/DatePickerInput'
import useInvalidDates from './hooks/useInvalidDates'
import type { DateFormatOptions } from '../../../../components/date-format/DateFormatUtils'
import { formatDate } from '../../../../components/date-format/DateFormatUtils'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

// `range`, `showInput`, `showCancelButton` and `showResetButton` are not picked from the `DatePickerProps`
// Since they require `Field.Date` specific comments, due to them having different default values
//
// `inline` is intentionally not supported in `Field.Date`.
// Use `DatePicker` directly if an inline calendar is needed.
export type DateValidator = ValidatorWithCustomValidators<
  string,
  {
    dateValidator: Validator<string>
  }
>

export type DateProps = Omit<
  FieldProps<string, undefined | string>,
  'onBlurValidator'
> & {
  // Validation
  pattern?: string
  /**
   * Defines if the Date field should support a value of two dates (starting and ending date).
   * The value needs to be a string containing two dates, separated by a pipe character (`|`) i.e. (`01-09-2024|30-09-2024`) when this is set to `true`.
   * Defaults to `false`.
   */
  range?: DatePickerProps['range']
  /**
   * If the input fields with the mask should be visible. Defaults to `true`.
   */
  showInput?: DatePickerProps['showInput']

  /**
   * If set to `true`, a cancel button will be shown. You can change the default text by using `cancelButtonText="Avbryt"`. Defaults to `true`. If the `range` prop is `true`, then the cancel button is shown.
   */
  showCancelButton?: DatePickerProps['showCancelButton']
  /**
   * If set to `true`, a reset button will be shown. You can change the default text by using `resetButtonText="Tilbakestill"`. When clicked, the field resets to the initial `value` or `defaultValue`. If no initial value was provided, the field is cleared. Defaults to `true`.
   */
  showResetButton?: DatePickerProps['showResetButton']
  onBlurValidator?: DateValidator | false
} & Pick<
    DatePickerProps,
    | 'month'
    | 'startMonth'
    | 'endMonth'
    | 'minDate'
    | 'maxDate'
    | 'maskOrder'
    | 'maskPlaceholder'
    | 'dateFormat'
    | 'returnFormat'
    | 'hideNavigation'
    | 'hideDays'
    | 'onlyMonth'
    | 'hideLastWeek'
    | 'disableAutofocus'
    | 'showSubmitButton'
    | 'submitButtonText'
    | 'cancelButtonText'
    | 'resetButtonText'
    | 'firstDay'
    | 'link'
    | 'size'
    | 'sync'
    | 'addonElement'
    | 'shortcuts'
    | 'open'
    | 'direction'
    | 'alignPicker'
    | 'onDaysRender'
    | 'onType'
    | 'onOpen'
    | 'onClose'
    | 'onSubmit'
    | 'onCancel'
    | 'onReset'
    | 'skipPortal'
    | 'yearNavigation'
    | 'tooltip'
  >

function DateComponent(props: DateProps): ReactElement {
  const { errorRequired, label: defaultLabel } = useTranslation().Date
  const { locale } = useContext(SharedContext)

  const { invalidDatesRef, setInvalidDates } = useInvalidDates()
  const lastEditedRangeDateRef = useRef<'start' | 'end'>('start')

  const errorMessages = useMemo(() => {
    return {
      'Field.errorRequired': errorRequired,
      'Field.errorPattern': errorRequired,
      ...props.errorMessages,
    }
  }, [props.errorMessages, errorRequired])

  const schema = useMemo<Schema<string>>(
    () => {
      if (props.schema) {
        return props.schema
      }

      return (p: DateProps) => {
        let s = z.string()
        if (p?.pattern) {
          try {
            s = s.regex(new RegExp(p.pattern, 'u'))
          } catch (_e) {
            // Ignore invalid regex patterns
          }
        }
        return s
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.schema, props.pattern]
  )

  const validateRequired = useCallback(
    (value: string, { required, error }) => {
      if (!required) {
        return undefined
      }

      if (props.range) {
        if (value) {
          const [startDate, endDate] = parseRangeValue(value)
          if (startDate && endDate) {
            // If both dates exists, we don't return an error
            return undefined
          }
        }
        return new FormError('Date.errorRequiredRange')
      }

      return !value || !isValid(parseISO(value)) ? error : undefined
    },
    [props.range]
  )

  const dateValidator = useCallback(
    (value: string) => {
      const invalidDateErrors = validateDate(invalidDatesRef.current)

      const rangeOrderErrors = validateRangeOrder({
        value,
        isRange: props.range,
        lastEditedDate: lastEditedRangeDateRef.current,
        locale,
      })

      // No need to validate min/max date if they are not provided
      if (!props.minDate && !props.maxDate) {
        return [...invalidDateErrors, ...rangeOrderErrors]
      }

      const dateLimitErrors = validateDateLimit({
        value,
        locale,
        minDate: props.minDate,
        maxDate: props.maxDate,
        isRange: props.range,
      })

      // Skip range order errors when date limit errors exist,
      // since exceeding minDate/maxDate is more specific
      return [
        ...invalidDateErrors,
        ...dateLimitErrors,
        ...(dateLimitErrors.length > 0 ? [] : rangeOrderErrors),
      ]
    },
    [props.maxDate, props.minDate, props.range, invalidDatesRef, locale]
  )

  const {
    onBlurValidator: propOnBlurValidator,
    onChangeValidator,
    ...restProps
  } = props

  const onBlurValidator = useMemo(() => {
    if (propOnBlurValidator === false) {
      return undefined
    }

    if (propOnBlurValidator) {
      return propOnBlurValidator
    }

    return dateValidator
  }, [propOnBlurValidator, dateValidator])

  const fromInput = useCallback(
    ({
      date,
      startDate,
      endDate,
      invalidDate,
      invalidStartDate,
      invalidEndDate,
    }: DatePickerEvent<ChangeEvent<HTMLInputElement>>) => {
      // Add to ref, for use in onBlurValidator, would be better if there was a way to add this to additional args
      setInvalidDates({
        invalidDate,
        invalidStartDate,
        invalidEndDate,
      })

      return props.range ? `${startDate}|${endDate}` : date
    },
    [props.range, setInvalidDates]
  )

  const preparedProps = {
    ...restProps,
    errorMessages,
    schema,
    fromInput,
    validateRequired,
    onBlurValidator,
    onChangeValidator,
    exportValidators: { dateValidator },
    invalidDatesRef,
  }

  const {
    id,
    path,
    itemPath,
    className,
    label,
    value: internalValue,
    hasError,
    disabled,
    htmlAttributes,
    handleError,
    handleFocus,
    handleBlur,
    handleChange,
    setChanged,
    setDisplayValue,
    range,
    showCancelButton = true,
    showResetButton = true,
    showInput = true,
    onReset,
    minDate,
    maxDate,
    width,
    ...rest
    // @ts-expect-error - strictFunctionTypes
  } = useFieldProps(preparedProps)

  const datePickerProps = pickDatePickerProps(rest)
  const initialValueRef = useRef(props.value ?? props.defaultValue)
  const valueOnOpenRef = useRef(internalValue)

  const handleOpen = useCallback(
    (event: DatePickerEvent<DisplayPickerEvent>) => {
      valueOnOpenRef.current = internalValue
      datePickerProps.onOpen?.(event)
    },
    [internalValue, datePickerProps.onOpen]
  )

  const handleCancel = useCallback(
    (event: DatePickerEvent<ReactMouseEvent<HTMLButtonElement>>) => {
      const revertValue = valueOnOpenRef.current

      if (range) {
        const [startDate, endDate] = parseRangeValue(revertValue)
        handleChange({
          startDate: startDate ?? undefined,
          endDate: endDate ?? undefined,
        })
      } else {
        handleChange({ date: revertValue ?? undefined })
      }

      datePickerProps.onCancel?.(event)
    },
    [handleChange, range, datePickerProps.onCancel]
  )

  const handleSubmit = useCallback(
    (event: DatePickerEvent<ReactMouseEvent<HTMLButtonElement>>) => {
      valueOnOpenRef.current = internalValue
      datePickerProps.onSubmit?.(event)
    },
    [internalValue, datePickerProps.onSubmit]
  )

  const handleReset = useCallback(
    (
      event: DatePickerEvent<
        ReactMouseEvent<HTMLButtonElement, MouseEvent>
      >
    ) => {
      const initialValue = initialValueRef.current

      if (initialValue) {
        if (range) {
          const [startDate, endDate] = parseRangeValue(initialValue)
          handleChange({
            startDate: startDate ?? undefined,
            endDate: endDate ?? undefined,
          })
        } else {
          handleChange({ date: initialValue })
        }

        onReset?.(event)

        return // stop here
      }

      const reset = {
        date: undefined,
        startDate: undefined,
        endDate: undefined,
        isValid: false,
      }
      handleChange(reset)
      setDisplayValue(undefined)
      onReset?.({
        ...event,
        ...reset,
      })
    },
    [handleChange, onReset, setDisplayValue, range]
  )
  const onFocus = useCallback(() => {
    handleFocus()
    handleError()
  }, [handleFocus, handleError])
  const onType = useCallback(
    (event: DatePickerEvent<ChangeEvent<HTMLInputElement>>) => {
      const { date, startDate, endDate, ...rest } = event

      if (props.range) {
        // Track which date the user last edited for range order error messages
        const [prevStart, prevEnd] = parseRangeValue(internalValue)
        if (startDate !== prevStart) {
          lastEditedRangeDateRef.current = 'start'
        } else if (endDate !== prevEnd) {
          lastEditedRangeDateRef.current = 'end'
        }

        const parsedStartDate = parseISO(startDate)
        const parsedEndDate = parseISO(endDate)
        if (isValid(parsedStartDate) || isValid(parsedEndDate)) {
          handleChange({
            ...(isValid(parsedStartDate) && { startDate }),
            ...(isValid(parsedEndDate) && { endDate }),
            ...rest,
          })
        } else {
          handleChange(rest)
        }
      } else if (isValid(parseISO(date))) {
        handleChange({ date, ...rest })
      } else {
        handleChange(rest)
      }

      // To ensure that the form can show the required error message while typing and blurring
      setChanged(true)
    },
    [handleChange, props.range, setChanged, internalValue]
  )

  const { value, startDate, endDate } = useMemo(() => {
    if (!range || !internalValue) {
      return {
        // Assign to null if falsy value, to properly clear input values
        value: internalValue ?? null,
        startDate: undefined,
        endDate: undefined,
      }
    }

    const [startDate, endDate] = parseRangeValue(internalValue)

    return {
      value: undefined,
      startDate,
      endDate,
    }
  }, [range, internalValue])

  useMemo(() => {
    if ((path || itemPath) && value) {
      setDisplayValue(formatDate(value, { locale }))
    }
  }, [itemPath, locale, path, setDisplayValue, value])

  const fieldBlockProps: FieldBlockProps = {
    forId: id,
    label: label ?? defaultLabel,
    className: clsx('dnb-forms-field-string', className),
    width,
    ...pickSpacingProps(props),
  }

  return (
    <FieldBlock {...fieldBlockProps}>
      <DatePicker
        id={id}
        date={value}
        disabled={disabled}
        showInput={showInput}
        alignPicker={
          showInput && (width === 'large' || width === 'stretch')
            ? 'right'
            : undefined
        }
        showCancelButton={showCancelButton}
        showResetButton={showResetButton}
        stretch={width !== undefined}
        startDate={startDate}
        endDate={endDate}
        minDate={minDate}
        maxDate={maxDate}
        status={hasError ? 'error' : undefined}
        range={range}
        onReset={handleReset}
        onType={onType} // To support validation while typing (e.g. required)
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={handleBlur}
        {...datePickerProps}
        onOpen={handleOpen}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        {...htmlAttributes}
      />
    </FieldBlock>
  )
}

export function parseRangeValue(
  value: DateProps['value']
): Array<string | null> {
  return (
    String(value)
      .split('|')
      // Assign to null if falsy value, to properly clear input values
      .map((value) => (/(undefined|null)/.test(value) ? null : value))
  )
}

// Validators
function validateDateLimit({
  value,
  isRange,
  locale,
  ...dates
}: {
  value: DateProps['value']
  minDate: DateProps['minDate']
  maxDate: DateProps['maxDate']
  isRange: DateProps['range']
  locale: ProviderProps['locale']
}): Array<FormError> {
  if ((!dates.minDate && !dates.maxDate) || !value) {
    return []
  }

  const [startDateParsed, endDateParsed] = parseRangeValue(value)

  const convertedMinDate = convertStringToDate(dates.minDate)
  const convertedMaxDate = convertStringToDate(dates.maxDate)
  const convertedStartDate = convertStringToDate(startDateParsed)
  const convertedEndDate = convertStringToDate(endDateParsed)

  // Set dates to the start of the day to compare the actual days, and not day and time
  const minDate = convertedMinDate
    ? startOfDay(convertedMinDate)
    : undefined
  const maxDate = convertedMaxDate
    ? startOfDay(convertedMaxDate)
    : undefined
  const startDate = convertedStartDate
    ? startOfDay(convertedStartDate)
    : undefined
  const endDate = convertedEndDate
    ? startOfDay(convertedEndDate)
    : undefined

  const isoDates = {
    minDate:
      dates.minDate instanceof Date
        ? dates.minDate.toISOString()
        : dates.minDate,
    maxDate:
      dates.maxDate instanceof Date
        ? dates.maxDate.toISOString()
        : dates.maxDate,
  }

  const options: DateFormatOptions = {
    locale,
    options: { dateStyle: 'long' },
  }

  const messages: Array<FormError> = []

  // Handle non range validation
  if (!isRange) {
    if (isBefore(startDate, minDate)) {
      messages.push(
        new FormError('Date.errorMinDate', {
          messageValues: { date: formatDate(isoDates.minDate, options) },
        })
      )
    }

    if (isAfter(startDate, maxDate)) {
      messages.push(
        new FormError('Date.errorMaxDate', {
          messageValues: { date: formatDate(isoDates.maxDate, options) },
        })
      )
    }

    return messages
  }

  // Start date validation
  if (isBefore(startDate, minDate)) {
    messages.push(
      new FormError('Date.errorStartDateMinDate', {
        messageValues: { date: formatDate(isoDates.minDate, options) },
      })
    )
  }

  if (isAfter(startDate, maxDate)) {
    messages.push(
      new FormError('Date.errorStartDateMaxDate', {
        messageValues: { date: formatDate(isoDates.maxDate, options) },
      })
    )
  }

  // End date validation
  if (isBefore(endDate, minDate)) {
    messages.push(
      new FormError('Date.errorEndDateMinDate', {
        messageValues: { date: formatDate(isoDates.minDate, options) },
      })
    )
  }

  if (isAfter(endDate, maxDate)) {
    messages.push(
      new FormError('Date.errorEndDateMaxDate', {
        messageValues: { date: formatDate(isoDates.maxDate, options) },
      })
    )
  }

  return messages
}

function validateRangeOrder({
  value,
  isRange,
  lastEditedDate,
  locale,
}: {
  value: DateProps['value']
  isRange: DateProps['range']
  lastEditedDate: 'start' | 'end'
  locale: ProviderProps['locale']
}) {
  if (!isRange || !value) {
    return []
  }

  const [startDateParsed, endDateParsed] = parseRangeValue(value)

  const convertedStartDate = convertStringToDate(startDateParsed)
  const convertedEndDate = convertStringToDate(endDateParsed)

  const startDate = convertedStartDate
    ? startOfDay(convertedStartDate)
    : undefined
  const endDate = convertedEndDate
    ? startOfDay(convertedEndDate)
    : undefined

  if (startDate && endDate && isAfter(startDate, endDate)) {
    const options: DateFormatOptions = {
      locale,
      options: { dateStyle: 'long' },
    }

    if (lastEditedDate === 'end') {
      return [
        new FormError('Date.errorEndDateMinDate', {
          messageValues: {
            date: formatDate(startDateParsed, options),
          },
        }),
      ]
    }

    return [
      new FormError('Date.errorStartDateMaxDate', {
        messageValues: {
          date: formatDate(endDateParsed, options),
        },
      }),
    ]
  }

  return []
}

function validateDate({
  invalidDate,
  invalidStartDate,
  invalidEndDate,
}: DatePickerInvalidDates): Array<FormError> {
  // Don't show error if the date is empty or contains only placeholder values
  if (invalidDate && !isEmptyOrPlaceholder(invalidDate)) {
    return [
      new FormError('Date.errorInvalidDate', {
        messageValues: { date: invalidDate },
      }),
    ]
  }

  const errors: Array<FormError> = []

  if (invalidStartDate && !isEmptyOrPlaceholder(invalidStartDate)) {
    errors.push(
      new FormError('Date.errorInvalidStartDate', {
        messageValues: { date: invalidStartDate },
      })
    )
  }

  if (invalidEndDate && !isEmptyOrPlaceholder(invalidEndDate)) {
    errors.push(
      new FormError('Date.errorInvalidEndDate', {
        messageValues: { date: invalidEndDate },
      })
    )
  }

  return errors
}

function isEmptyOrPlaceholder(date: string): boolean {
  if (!date) {
    return true
  }

  // Check if the date is empty or contains only non-numeric characters
  // This covers placeholder patterns like "dd", "mm", "yyyy", "åååå", etc.
  return !/\d/.test(date)
}

// Used to filter out DatePickerProps from the FieldProps.
// Includes DatePickerProps that are not destructured in useFieldProps
const datePickerPropKeys = [
  'month',
  'startMonth',
  'endMonth',
  'minDate',
  'maxDate',
  'maskOrder',
  'maskPlaceholder',
  'dateFormat',
  'returnFormat',
  'hideNavigation',
  'hideDays',
  'onlyMonth',
  'hideLastWeek',
  'disableAutofocus',
  'showSubmitButton',
  'submitButtonText',
  'cancelButtonText',
  'resetButtonText',
  'firstDay',
  'link',
  'size',
  'sync',
  'addonElement',
  'shortcuts',
  'open',
  'direction',
  'alignPicker',
  'onDaysRender',
  'showInput',
  'onDaysRender',
  'onType',
  'onOpen',
  'onClose',
  'onSubmit',
  'onCancel',
  'onReset',
  'skipPortal',
  'yearNavigation',
  'tooltip',
]

function pickDatePickerProps(props: DateProps): Partial<DatePickerProps> {
  const datePickerProps = Object.keys(props).reduce(
    (datePickerProps, key) => {
      if (datePickerPropKeys.includes(key)) {
        datePickerProps[key] = props[key]
      }

      return datePickerProps
    },
    {} as Partial<DatePickerProps>
  )

  return datePickerProps
}

withComponentMarkers(DateComponent, {
  _supportsSpacingProps: true,
})

export default DateComponent
